import React, { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { IntegrationsEditor } from "@/components/IntegrationsEditor";
import { ClaudeConnector } from "@/components/ClaudeConnector";

export default function Settings() {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const [tab, setTab] = useState(params.get("tab") || "categories");

    if (user?.role !== "admin") {
        return (
            <div className="p-6" data-testid="settings-page">
                <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900 mb-4">Collegamento a Claude</h1>
                <ClaudeConnector />
            </div>
        );
    }

    return (
        <div className="p-6" data-testid="settings-page">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900 mb-4">Impostazioni</h1>
            <div className="flex gap-2 mb-4 border-b border-neutral-200">
                {[
                    ["categories", "Categorie clienti"],
                    ["note-types", "Tipi di nota"],
                    ["statuses", "Stati progetto"],
                    ["users", "Utenti"],
                    ["preferences", "Preferenze & Email"],
                    ["integrations", "Integrazioni"],
                    ["claude", "Collegamento a Claude"],
                ].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                        className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === k ? "border-[#FFE500] text-neutral-900 font-semibold" : "border-transparent text-neutral-500 hover:text-neutral-700"}`}>{l}</button>
                ))}
            </div>
            {tab === "categories" && <CategoriesEditor />}
            {tab === "note-types" && <NoteTypesEditor />}
            {tab === "statuses" && <StatusesEditor />}
            {tab === "users" && <UsersEditor />}
            {tab === "preferences" && <PreferencesEditor />}
            {tab === "integrations" && <IntegrationsEditor />}
            {tab === "claude" && <ClaudeConnector />}
        </div>
    );
}

function useCrud(endpoint, defaultItem) {
    const [items, setItems] = useState([]);
    const load = async () => setItems((await api.get(endpoint)).data);
    useEffect(() => { load(); }, []); // eslint-disable-line
    const add = async () => { await api.post(endpoint, defaultItem); load(); };
    const update = async (id, body) => { await api.patch(`${endpoint}/${id}`, body); load(); };
    const remove = async (id) => { if (confirm("Eliminare?")) { await api.delete(`${endpoint}/${id}`); load(); } };
    return { items, add, update, remove };
}

function CategoriesEditor() {
    const { items, add, update, remove } = useCrud("/settings/categories", { name: "Nuova categoria", color: "#64748b", order: 0 });
    return (
        <ListEditor title="Categorie clienti" items={items} onAdd={add} onRemove={remove} onUpdate={update} testId="categories-editor" fields={[
            { key: "name", label: "Nome" }, { key: "color", label: "Colore", type: "color" }, { key: "order", label: "Ordine", type: "number" },
        ]} />
    );
}
function NoteTypesEditor() {
    const { items, add, update, remove } = useCrud("/settings/note-types", { name: "Nuovo tipo", key: "custom_" + Date.now(), color: "#64748b", order: 0 });
    return (
        <ListEditor title="Tipi di nota" items={items} onAdd={add} onRemove={remove} onUpdate={update} testId="note-types-editor" fields={[
            { key: "name", label: "Nome" }, { key: "key", label: "Chiave" }, { key: "color", label: "Colore", type: "color" }, { key: "order", label: "Ordine", type: "number" },
        ]} />
    );
}
function StatusesEditor() {
    const { items, add, update, remove } = useCrud("/settings/project-statuses", { name: "Nuovo stato", color: "#64748b", order: 0, is_final: false });
    return (
        <ListEditor title="Stati progetto" items={items} onAdd={add} onRemove={remove} onUpdate={update} testId="statuses-editor" fields={[
            { key: "name", label: "Nome" }, { key: "color", label: "Colore", type: "color" }, { key: "order", label: "Ordine", type: "number" }, { key: "is_final", label: "Finale", type: "checkbox" },
        ]} />
    );
}

function ListEditor({ title, items, onAdd, onRemove, onUpdate, fields, testId }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-md" data-testid={testId}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
                <div className="font-display text-sm font-semibold">{title} · {items.length}</div>
                <button onClick={onAdd} className="text-xs bg-neutral-900 text-white px-2 py-1 rounded inline-flex items-center gap-1"><Plus size={12} /> Aggiungi</button>
            </div>
            <ul className="divide-y divide-neutral-100">
                {items.map((it) => (
                    <li key={it.id} className="px-4 py-2 flex items-center gap-2 flex-wrap">
                        {fields.map((f) => {
                            const value = it[f.key];
                            if (f.type === "color") return (
                                <input key={f.key} type="color" value={value || "#64748b"} onChange={(e) => onUpdate(it.id, { ...it, [f.key]: e.target.value })} className="w-8 h-7 border border-neutral-200 rounded" />
                            );
                            if (f.type === "number") return (
                                <input key={f.key} type="number" value={value || 0} onChange={(e) => onUpdate(it.id, { ...it, [f.key]: parseInt(e.target.value) || 0 })} className="w-16 text-xs border border-neutral-200 rounded px-1.5 py-1" />
                            );
                            if (f.type === "checkbox") return (
                                <label key={f.key} className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!value} onChange={(e) => onUpdate(it.id, { ...it, [f.key]: e.target.checked })} /> {f.label}</label>
                            );
                            return (
                                <input key={f.key} value={value || ""} onChange={(e) => onUpdate(it.id, { ...it, [f.key]: e.target.value })} className="text-sm border border-neutral-200 rounded px-2 py-1 flex-1 min-w-[120px]" placeholder={f.label} />
                            );
                        })}
                        <button onClick={() => onRemove(it.id)} className="text-neutral-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function UsersEditor() {
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "collaborator" });
    const [err, setErr] = useState("");
    const load = async () => setUsers((await api.get("/users")).data);
    useEffect(() => { load(); }, []);
    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try { await api.post("/users", form); setForm({ first_name: "", last_name: "", email: "", password: "", role: "collaborator" }); load(); }
        catch (e) { setErr(extractError(e)); }
    };
    const setRole = async (u, role) => { await api.patch(`/users/${u.id}`, { role }); load(); };
    const toggleActive = async (u) => { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); load(); };
    const removeUser = async (u) => {
        if (!window.confirm(`Eliminare l'utente ${u.first_name} ${u.last_name}? L'utente non potrà più accedere con le sue credenziali.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            load();
        } catch (e) { alert(extractError(e)); }
    };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="users-editor">
            <div className="bg-white border border-neutral-200 rounded-md lg:col-span-2">
                <div className="px-4 py-3 border-b border-neutral-100 font-display text-sm font-semibold">Utenti · {users.length}</div>
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr><th className="text-left px-4 py-2">Nome</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Ruolo</th><th className="text-left px-4 py-2">Attivo</th><th className="text-left px-4 py-2"></th></tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t border-neutral-100" data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-2 font-medium">{u.first_name} {u.last_name}</td>
                                <td className="px-4 py-2 text-neutral-600">{u.email}</td>
                                <td className="px-4 py-2">
                                    <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className="text-xs border border-neutral-200 rounded px-1.5 py-0.5">
                                        <option value="admin">Amministratore</option>
                                        <option value="collaborator">Collaboratore</option>
                                    </select>
                                </td>
                                <td className="px-4 py-2"><input type="checkbox" checked={!!u.is_active} onChange={() => toggleActive(u)} /></td>
                                <td className="px-4 py-2 text-right">
                                    <button onClick={() => removeUser(u)} data-testid={`delete-user-${u.id}`} className="text-red-500 hover:text-red-700" title="Elimina utente">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-md p-4" data-testid="new-user-form">
                <div className="font-display text-sm font-semibold mb-3">Nuovo utente</div>
                {["first_name", "last_name", "email", "password"].map((k) => (
                    <label key={k} className="block mb-2">
                        <span className="text-xs text-neutral-600">{k === "first_name" ? "Nome" : k === "last_name" ? "Cognome" : k === "email" ? "Email" : "Password"}</span>
                        <input required type={k === "password" ? "password" : k === "email" ? "email" : "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full text-sm border border-neutral-300 rounded px-2 py-1.5" />
                    </label>
                ))}
                <label className="block mb-3">
                    <span className="text-xs text-neutral-600">Ruolo</span>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full text-sm border border-neutral-300 rounded px-2 py-1.5">
                        <option value="admin">Amministratore</option>
                        <option value="collaborator">Collaboratore</option>
                    </select>
                </label>
                {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">{err}</div>}
                <button type="submit" className="w-full text-sm bg-neutral-900 text-white rounded px-3 py-1.5 hover:bg-neutral-800">Crea utente</button>
            </form>
        </div>
    );
}

function PreferencesEditor() {
    const [prefs, setPrefs] = useState(null);
    useEffect(() => { api.get("/settings/preferences").then((r) => setPrefs(r.data)); }, []);
    if (!prefs) return <div className="text-sm text-neutral-500">Caricamento…</div>;
    const save = async () => { await api.patch("/settings/preferences", prefs); alert("Preferenze salvate"); };
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-5 max-w-2xl space-y-4" data-testid="preferences-editor">
            <div>
                <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!prefs.show_completion_percentage} onChange={(e) => setPrefs({ ...prefs, show_completion_percentage: e.target.checked })} />
                    Mostra percentuali di completamento su programmi e task
                </label>
            </div>
            <div>
                <label className="block text-xs text-neutral-600 mb-1">Link questionario esterno (Typeform / Google Forms)</label>
                <input value={prefs.feedback_form_url || ""} onChange={(e) => setPrefs({ ...prefs, feedback_form_url: e.target.value })} placeholder="https://…" className="w-full text-sm border border-neutral-300 rounded px-2 py-1.5" />
            </div>
            <div>
                <label className="block text-xs text-neutral-600 mb-1">Oggetto email automatica</label>
                <input value={prefs.email_template_subject || ""} onChange={(e) => setPrefs({ ...prefs, email_template_subject: e.target.value })} className="w-full text-sm border border-neutral-300 rounded px-2 py-1.5" />
            </div>
            <div>
                <label className="block text-xs text-neutral-600 mb-1">Corpo email (campi disponibili: {"{client_name}"}, {"{project_name}"}, {"{form_url}"})</label>
                <textarea rows={6} value={prefs.email_template_body || ""} onChange={(e) => setPrefs({ ...prefs, email_template_body: e.target.value })} className="w-full text-sm border border-neutral-300 rounded px-2 py-1.5 font-mono" />
            </div>
            <button onClick={save} className="text-sm bg-neutral-900 text-white rounded px-3 py-1.5 hover:bg-neutral-800">Salva preferenze</button>
        </div>
    );
}
